import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { useLanguage } from '../../context/LanguageContext';

const API = '/api/v1/vsm';

const VSMClients = () => {
  const { lang } = useLanguage();
  const isRTL = lang === 'ar';
  const navigate = useNavigate();

  const T = {
    title:           { ar: '🚗 عملاء خدمات السيارات', en: '🚗 Vehicle Service Clients' },
    subtitle:        { ar: 'عميل', en: 'client(s)' },
    add:             { ar: '+ إضافة عميل', en: '+ Add Client' },
    edit:            { ar: '✏️ تعديل بيانات العميل', en: '✏️ Edit Client' },
    create:          { ar: '➕ عميل جديد', en: '➕ New Client' },
    search:          { ar: 'ابحث بالاسم أو الجوال...', en: 'Search by name or phone...' },
    basicInfo:       { ar: 'البيانات الأساسية', en: 'Basic Information' },
    contactInfo:     { ar: 'بيانات التواصل', en: 'Contact Information' },
    locationInfo:    { ar: 'بيانات العنوان', en: 'Location' },
    nameEn:          { ar: 'الاسم (إنجليزي)', en: 'Name (English)' },
    nameAr:          { ar: 'الاسم (عربي)', en: 'Name (Arabic)' },
    phone:           { ar: 'رقم الجوال', en: 'Phone' },
    whatsapp:        { ar: 'رقم الواتساب', en: 'WhatsApp Number' },
    whatsappHint:    { ar: 'لإرسال إشعارات تأكيد الخدمة', en: 'For service confirmation notifications' },
    email:           { ar: 'البريد الإلكتروني', en: 'Email' },
    address:         { ar: 'العنوان', en: 'Address' },
    city:            { ar: 'المدينة', en: 'City' },
    notes:           { ar: 'ملاحظات', en: 'Notes' },
    active:          { ar: 'نشط', en: 'Active' },
    assignmentInfo:  { ar: 'التعيين', en: 'Assignment' },
    team:            { ar: 'الفريق', en: 'Team' },
    assignedAgent:   { ar: 'الموظف المسؤول', en: 'Assigned Agent' },
    none:            { ar: '-- لا شيء --', en: '-- None --' },
    save:            { ar: '💾 حفظ', en: '💾 Save' },
    saving:          { ar: 'جاري الحفظ...', en: 'Saving...' },
    cancel:          { ar: 'إلغاء', en: 'Cancel' },
    delete:          { ar: '🗑️ حذف', en: '🗑️ Delete' },
    confirmDelete:   { ar: 'هل أنت متأكد من حذف هذا العميل؟', en: 'Are you sure you want to delete this client?' },
    vehicles:        { ar: 'السيارات', en: 'Vehicles' },
    contracts:       { ar: 'العقود النشطة', en: 'Active Contracts' },
    actions:         { ar: 'الإجراءات', en: 'Actions' },
    addVehicle:      { ar: 'إضافة سيارة', en: 'Add Vehicle' },
    loading:         { ar: 'جاري التحميل...', en: 'Loading...' },
    noClients:       { ar: 'لا يوجد عملاء حتى الآن. اضغط "إضافة عميل" للبدء.', en: 'No clients yet. Click "Add Client" to start.' },
    prev:            { ar: '« السابق', en: '« Previous' },
    next:            { ar: 'التالي »', en: 'Next »' },
    page:            { ar: 'صفحة', en: 'Page' },
    of:              { ar: 'من', en: 'of' },
    nameRequired:    { ar: 'اسم العميل مطلوب', en: 'Client name is required' },
    saveError:       { ar: 'فشل حفظ البيانات', en: 'Failed to save' },
    deleteError:     { ar: 'فشل حذف العميل', en: 'Failed to delete' },
  };

  const tr = (key) => T[key]?.[lang] || T[key]?.en || key;

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({ total: 0, page: 1, total_pages: 1 });
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Teams + Agents for assignment dropdowns
  const [teams, setTeams] = useState([]);
  const [agents, setAgents] = useState([]);
  const [teamMemberAgents, setTeamMemberAgents] = useState(null); // null = no team filter
  useEffect(() => {
    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}` };
    fetch('/api/v1/teams', { headers })
      .then(r => r.json())
      .then(d => { if (d.success) setTeams(d.data || []); })
      .catch(() => {});
    fetch('/api/v1/teams/agents/available', { headers })
      .then(r => r.json())
      .then(d => { if (d.success) setAgents(d.data || []); })
      .catch(() => {});
  }, []);


  const emptyForm = {
    name: '', name_ar: '', phone: '', whatsapp: '',
    email: '', address: '', city: '', notes: '', is_active: true,
    team_id: '', assigned_agent_id: ''
  };
  const [form, setForm] = useState({ ...emptyForm });

  // When form.team_id changes, fetch members of that team to filter the agent dropdown
  useEffect(() => {
    if (!form.team_id) {
      setTeamMemberAgents(null);
      return;
    }
    const token = localStorage.getItem('token');
    fetch(`/api/v1/teams/${form.team_id}/agents`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(d => {
        if (d.success) {
          setTeamMemberAgents(d.data || []);
          const memberIds = (d.data || []).map(a => a.id);
          setForm(prev => {
            if (prev.assigned_agent_id && !memberIds.includes(parseInt(prev.assigned_agent_id, 10))) {
              return { ...prev, assigned_agent_id: '' };
            }
            return prev;
          });
        }
      })
      .catch(() => setTeamMemberAgents(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.team_id]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const fetchClients = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

    const params = new URLSearchParams({ page: pagination.page, limit: 15 });
    if (search) params.append('search', search);

    try {
      const res = await fetch(`${API}/clients?${params}`, { headers });
      const data = await res.json();
      if (data.success) {
        setClients(data.data);
        setPagination(prev => ({ ...prev, ...data.pagination }));
      }
    } catch (err) {
      console.error('[VSM] Fetch clients error:', err);
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, pagination.page]);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  const resetForm = () => { setForm({ ...emptyForm }); setEditId(null); };

  const openCreate = () => {
    resetForm();
    setShowForm(true);
  };

  const openEdit = (client, e) => {
    e?.stopPropagation();
    setForm({
      name: client.name || '',
      name_ar: client.name_ar || '',
      phone: client.phone || '',
      whatsapp: client.whatsapp || '',
      email: client.email || '',
      address: client.address || '',
      city: client.city || '',
      notes: client.notes || '',
      is_active: !!client.is_active,
      team_id: client.team_id || '',
      assigned_agent_id: client.assigned_agent_id || ''
    });
    setEditId(client.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.name.trim()) {
      alert(tr('nameRequired'));
      return;
    }
    setSaving(true);

    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

    try {
      const url = editId ? `${API}/clients/${editId}` : `${API}/clients`;
      const method = editId ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method, headers, body: JSON.stringify(form)
      });
      const data = await res.json();
      if (data.success) {
        setShowForm(false);
        resetForm();
        fetchClients();
      } else {
        alert(data.error || tr('saveError'));
      }
    } catch (err) {
      console.error('[VSM] Save client error:', err);
      alert(tr('saveError'));
    }
    setSaving(false);
  };

  const handleDelete = async (client, e) => {
    e?.stopPropagation();
    if (!window.confirm(`${tr('confirmDelete')}\n\n${client.name}`)) return;

    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

    try {
      const res = await fetch(`${API}/clients/${client.id}`, { method: 'DELETE', headers });
      const data = await res.json();
      if (data.success) {
        fetchClients();
      } else {
        alert(data.error || tr('deleteError'));
      }
    } catch (err) {
      console.error('[VSM] Delete client error:', err);
      alert(tr('deleteError'));
    }
  };

  const handleAddVehicle = (client, e) => {
    e?.stopPropagation();
    navigate(`/vsm/vehicles?client_id=${client.id}&new=1`);
  };

  // Styles
  const inputStyle = {
    width: '100%', padding: '12px', borderRadius: '8px',
    border: '2px solid rgba(255,255,255,0.2)', backgroundColor: '#1a2038',
    color: '#f9fafb', fontSize: '13px', outline: 'none', boxSizing: 'border-box'
  };
  const labelStyle = {
    fontSize: '11px', color: '#94a3b8', fontWeight: '600',
    display: 'block', marginBottom: '4px'
  };
  const sectionStyle = {
    background: 'rgba(15,23,42,0.3)', borderRadius: '10px',
    padding: '16px', marginBottom: '14px',
    border: '1px solid rgba(255,255,255,0.06)'
  };
  const sectionTitle = (icon, text) => (
    <h3 style={{
      fontSize: '13px', fontWeight: '700', color: '#818cf8',
      margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: '6px'
    }}>{icon} {text}</h3>
  );
  const modalOverlay = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
  };
  const modalBox = {
    background: '#1e293b', borderRadius: '16px', padding: '24px',
    maxHeight: '90vh', overflow: 'auto',
    border: '1px solid rgba(255,255,255,0.1)',
    direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left'
  };
  const tableHeaderStyle = {
    padding: '10px 12px', fontSize: '12px', color: '#94a3b8',
    fontWeight: '600', textAlign: isRTL ? 'right' : 'left',
    borderBottom: '1px solid rgba(255,255,255,0.08)'
  };
  const tableCellStyle = {
    padding: '12px', fontSize: '13px', color: '#e2e8f0',
    borderBottom: '1px solid rgba(255,255,255,0.04)'
  };

  return (
    <Layout>
      <div style={{
        padding: '24px', maxWidth: '1300px', margin: '0 auto',
        direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left'
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#f1f5f9', margin: 0 }}>
              {tr('title')}
            </h1>
            <p style={{ fontSize: '13px', color: '#94a3b8', margin: '4px 0 0' }}>
              {pagination.total} {tr('subtitle')}
            </p>
          </div>
          <button
            onClick={openCreate}
            style={{
              background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: '#fff',
              border: 'none', padding: '9px 18px', borderRadius: '8px',
              fontWeight: '600', fontSize: '13px', cursor: 'pointer'
            }}
          >
            {tr('add')}
          </button>
        </div>

        {/* Search */}
        <input
          type="text"
          placeholder={tr('search')}
          value={search}
          onChange={e => {
            setSearch(e.target.value);
            setPagination(p => ({ ...p, page: 1 }));
          }}
          style={{ ...inputStyle, maxWidth: '350px', marginBottom: '16px' }}
        />

        {/* Table */}
        <div style={{
          background: 'rgba(30,41,59,0.5)', borderRadius: '12px',
          overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)'
        }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
              {tr('loading')}
            </div>
          ) : clients.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
              {tr('noClients')}
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ background: 'rgba(15,23,42,0.5)' }}>
                <tr>
                  <th style={tableHeaderStyle}>{tr('nameEn')}</th>
                  <th style={tableHeaderStyle}>{tr('phone')}</th>
                  <th style={tableHeaderStyle}>{tr('whatsapp')}</th>
                  <th style={tableHeaderStyle}>{tr('city')}</th>
                  <th style={{ ...tableHeaderStyle, textAlign: 'center' }}>{tr('vehicles')}</th>
                  <th style={{ ...tableHeaderStyle, textAlign: 'center' }}>{tr('contracts')}</th>
                  <th style={{ ...tableHeaderStyle, textAlign: 'center' }}>{tr('actions')}</th>
                </tr>
              </thead>
              <tbody>
                {clients.map(client => (
                  <tr key={client.id} style={{ transition: 'background 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(99,102,241,0.05)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={tableCellStyle}>
                      <div style={{ fontWeight: '600', color: '#f1f5f9' }}>
                        {isRTL && client.name_ar ? client.name_ar : client.name}
                      </div>
                      {client.name_ar && !isRTL && (
                        <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                          {client.name_ar}
                        </div>
                      )}
                      {!client.is_active && (
                        <span style={{
                          display: 'inline-block', marginTop: '4px',
                          background: 'rgba(239,68,68,0.15)', color: '#fca5a5',
                          fontSize: '10px', padding: '2px 6px', borderRadius: '4px'
                        }}>
                          Inactive
                        </span>
                      )}
                    </td>
                    <td style={tableCellStyle}>{client.phone || '—'}</td>
                    <td style={tableCellStyle}>
                      {client.whatsapp ? (
                        <span style={{ color: '#10b981' }}>📱 {client.whatsapp}</span>
                      ) : '—'}
                    </td>
                    <td style={tableCellStyle}>{client.city || '—'}</td>
                    <td style={{ ...tableCellStyle, textAlign: 'center' }}>
                      <span style={{
                        background: 'rgba(99,102,241,0.15)', color: '#a5b4fc',
                        padding: '4px 10px', borderRadius: '12px',
                        fontSize: '12px', fontWeight: '600'
                      }}>
                        {client.vehicle_count || 0}
                      </span>
                    </td>
                    <td style={{ ...tableCellStyle, textAlign: 'center' }}>
                      <span style={{
                        background: 'rgba(16,185,129,0.15)', color: '#6ee7b7',
                        padding: '4px 10px', borderRadius: '12px',
                        fontSize: '12px', fontWeight: '600'
                      }}>
                        {client.active_contracts || 0}
                      </span>
                    </td>
                    <td style={{ ...tableCellStyle, textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                        <button
                          onClick={(e) => handleAddVehicle(client, e)}
                          title={tr('addVehicle')}
                          style={{
                            background: 'rgba(16,185,129,0.2)', color: '#6ee7b7',
                            border: '1px solid rgba(16,185,129,0.3)',
                            padding: '5px 10px', borderRadius: '6px',
                            fontSize: '12px', cursor: 'pointer', fontWeight: '600',
                            display: 'flex', alignItems: 'center', gap: '4px'
                          }}
                        >
                          🚙+
                        </button>
                        <button
                          onClick={(e) => openEdit(client, e)}
                          style={{
                            background: 'rgba(99,102,241,0.2)', color: '#a5b4fc',
                            border: '1px solid rgba(99,102,241,0.3)',
                            padding: '5px 10px', borderRadius: '6px',
                            fontSize: '12px', cursor: 'pointer', fontWeight: '600'
                          }}
                        >
                          ✏️
                        </button>
                        <button
                          onClick={(e) => handleDelete(client, e)}
                          style={{
                            background: 'rgba(239,68,68,0.15)', color: '#fca5a5',
                            border: '1px solid rgba(239,68,68,0.25)',
                            padding: '5px 10px', borderRadius: '6px',
                            fontSize: '12px', cursor: 'pointer', fontWeight: '600'
                          }}
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div style={{
            display: 'flex', justifyContent: 'center', alignItems: 'center',
            gap: '12px', marginTop: '16px'
          }}>
            <button
              disabled={pagination.page <= 1}
              onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
              style={{
                background: pagination.page <= 1 ? 'rgba(100,116,139,0.2)' : 'rgba(99,102,241,0.2)',
                color: pagination.page <= 1 ? '#64748b' : '#a5b4fc',
                border: '1px solid rgba(99,102,241,0.3)',
                padding: '8px 16px', borderRadius: '8px',
                fontSize: '13px', cursor: pagination.page <= 1 ? 'not-allowed' : 'pointer'
              }}
            >
              {tr('prev')}
            </button>
            <span style={{ color: '#94a3b8', fontSize: '13px' }}>
              {tr('page')} {pagination.page} {tr('of')} {pagination.total_pages}
            </span>
            <button
              disabled={pagination.page >= pagination.total_pages}
              onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
              style={{
                background: pagination.page >= pagination.total_pages ? 'rgba(100,116,139,0.2)' : 'rgba(99,102,241,0.2)',
                color: pagination.page >= pagination.total_pages ? '#64748b' : '#a5b4fc',
                border: '1px solid rgba(99,102,241,0.3)',
                padding: '8px 16px', borderRadius: '8px',
                fontSize: '13px', cursor: pagination.page >= pagination.total_pages ? 'not-allowed' : 'pointer'
              }}
            >
              {tr('next')}
            </button>
          </div>
        )}

        {/* Client Form Modal */}
        {showForm && (
          <div style={modalOverlay} onClick={() => setShowForm(false)}>
            <div style={{ ...modalBox, width: '650px', maxWidth: '95vw' }} onClick={e => e.stopPropagation()}>
              <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#f1f5f9', margin: '0 0 16px' }}>
                {editId ? tr('edit') : tr('create')}
              </h2>
              <form onSubmit={handleSubmit}>

                <div style={sectionStyle}>
                  {sectionTitle('🏷️', tr('basicInfo'))}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    <div>
                      <label style={labelStyle}>{tr('nameEn')} *</label>
                      <input
                        value={form.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        style={inputStyle}
                        required
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>{tr('nameAr')}</label>
                      <input
                        value={form.name_ar}
                        onChange={(e) => handleChange('name_ar', e.target.value)}
                        style={{ ...inputStyle, textAlign: 'right' }}
                        dir="rtl"
                      />
                    </div>
                  </div>
                  <div style={{ marginTop: '10px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: '#cbd5e1' }}>
                      <input
                        type="checkbox"
                        checked={form.is_active}
                        onChange={(e) => handleChange('is_active', e.target.checked)}
                        style={{ width: '16px', height: '16px' }}
                      />
                      {tr('active')}
                    </label>
                  </div>
                </div>

                <div style={sectionStyle}>
                  {sectionTitle('📞', tr('contactInfo'))}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    <div>
                      <label style={labelStyle}>{tr('phone')}</label>
                      <input
                        value={form.phone}
                        onChange={(e) => handleChange('phone', e.target.value)}
                        style={inputStyle}
                        placeholder="+966501234567"
                        dir="ltr"
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>{tr('whatsapp')}</label>
                      <input
                        value={form.whatsapp}
                        onChange={(e) => handleChange('whatsapp', e.target.value)}
                        style={inputStyle}
                        placeholder="+966501234567"
                        dir="ltr"
                      />
                      <div style={{ fontSize: '10px', color: '#64748b', marginTop: '4px' }}>
                        {tr('whatsappHint')}
                      </div>
                    </div>
                  </div>
                  <div style={{ marginTop: '10px' }}>
                    <label style={labelStyle}>{tr('email')}</label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      style={inputStyle}
                      dir="ltr"
                    />
                  </div>
                </div>

                <div style={sectionStyle}>
                  {sectionTitle('🤝', tr('assignmentInfo'))}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    <div>
                      <label style={labelStyle}>{tr('team')}</label>
                      <select
                        value={form.team_id}
                        onChange={(e) => handleChange('team_id', e.target.value)}
                        style={inputStyle}
                      >
                        <option value="">{tr('none')}</option>
                        {teams.map(t => (
                          <option key={t.id} value={t.id}>
                            {lang === 'ar' && t.name_ar ? t.name_ar : t.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle}>{tr('assignedAgent')}</label>
                      <select
                        value={form.assigned_agent_id}
                        onChange={(e) => handleChange('assigned_agent_id', e.target.value)}
                        style={inputStyle}
                      >
                        <option value="">{tr('none')}</option>
                        {(teamMemberAgents !== null ? teamMemberAgents : agents).map(a => (
                          <option key={a.id} value={a.id}>
                            {a.full_name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div style={sectionStyle}>
                  {sectionTitle('📍', tr('locationInfo'))}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    <div>
                      <label style={labelStyle}>{tr('city')}</label>
                      <input
                        value={form.city}
                        onChange={(e) => handleChange('city', e.target.value)}
                        style={inputStyle}
                      />
                    </div>
                  </div>
                  <div style={{ marginTop: '10px' }}>
                    <label style={labelStyle}>{tr('address')}</label>
                    <textarea
                      value={form.address}
                      onChange={(e) => handleChange('address', e.target.value)}
                      style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }}
                      rows={2}
                    />
                  </div>
                  <div style={{ marginTop: '10px' }}>
                    <label style={labelStyle}>{tr('notes')}</label>
                    <textarea
                      value={form.notes}
                      onChange={(e) => handleChange('notes', e.target.value)}
                      style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }}
                      rows={2}
                    />
                  </div>
                </div>

                <div style={{
                  display: 'flex', gap: '10px', justifyContent: 'flex-end',
                  marginTop: '16px', flexDirection: isRTL ? 'row-reverse' : 'row'
                }}>
                  <button
                    type="button"
                    onClick={() => { setShowForm(false); resetForm(); }}
                    style={{
                      background: 'rgba(100,116,139,0.2)', color: '#cbd5e1',
                      border: '1px solid rgba(100,116,139,0.3)',
                      padding: '10px 20px', borderRadius: '8px',
                      fontSize: '13px', cursor: 'pointer', fontWeight: '600'
                    }}
                  >
                    {tr('cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    style={{
                      background: saving ? 'rgba(99,102,241,0.3)' : 'linear-gradient(135deg,#6366f1,#8b5cf6)',
                      color: '#fff', border: 'none',
                      padding: '10px 24px', borderRadius: '8px',
                      fontSize: '13px', cursor: saving ? 'not-allowed' : 'pointer',
                      fontWeight: '700'
                    }}
                  >
                    {saving ? tr('saving') : tr('save')}
                  </button>
                </div>

              </form>
            </div>
          </div>
        )}

      </div>
    </Layout>
  );
};

export default VSMClients;
